import React from 'react';
import { Sparkles, Star, Search, Sliders, Plug, Clock, GraduationCap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
const SERVICE_LOGOS: Record<string, string> = {
  'Wix AI': 'W',
  'Framer AI': 'F',
  'Durable AI': 'D',
  'Hostinger AI': 'H',
  '10Web AI': '10'
};
const LOGO_COLORS: Record<string, string> = {
  'Wix AI': 'bg-yellow-500',
  'Framer AI': 'bg-blue-500',
  'Durable AI': 'bg-emerald-500',
  'Hostinger AI': 'bg-purple-600',
  '10Web AI': 'bg-rose-500'
};
export function TopServices() {
  const {
    t
  } = useLanguage();
  const services = t.topServices.services;
  const labels = t.topServices.labels;
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useScrollAnimation();
  return <section id="rating" className="py-24 bg-[#050505]" aria-labelledby="rating-title" itemScope itemType="https://schema.org/ItemList" data-id="element-419">
      <meta itemProp="name" content={t.topServices.title} data-id="element-420" />
      <meta itemProp="description" content={t.topServices.subtitle} data-id="element-421" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-422">
        <div ref={titleRef} className={`text-center mb-12 animate-hidden animate-fade-up ${titleVisible ? 'animate-visible' : ''}`} data-id="element-423">
          <h2 id="rating-title" className="text-3xl md:text-4xl font-bold text-white mb-4" itemProp="headline" data-id="element-424">
            {t.topServices.title}
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto" data-id="element-425">
            {t.topServices.subtitle}
          </p>
        </div>

        <ol className="space-y-4" role="list" aria-label={t.topServices.title} data-id="element-426">
          {services.map((service: any, index: number) => <li key={index} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" data-id="element-427">
              <meta itemProp="position" content={String(index + 1)} data-id="element-428" />
              <ServiceCard service={service} index={index} labels={labels} t={t} data-id="element-429" />
            </li>)}
        </ol>

        <aside className="mt-12 text-center max-w-4xl mx-auto" data-id="element-430">
          <p className="text-gray-400 text-sm leading-relaxed" data-id="element-431">
            {t.topServices.footerText}
          </p>
          <p className="text-gray-500 text-xs mt-2 italic" data-id="element-432">
            {t.topServices.ratingDisclaimer}
          </p>
        </aside>
      </div>

      {/* SEO: noscript fallback for crawlers that don't execute JS */}
      <noscript data-id="element-433">
        <div style={{
        padding: '2rem',
        color: '#fff',
        background: '#050505'
      }} data-id="element-434">
          <h2 data-id="element-435">{t.topServices.title}</h2>
          <p data-id="element-436">{t.topServices.subtitle}</p>
          <ol data-id="element-437">
            {services.map((service: any, index: number) => <li key={index} data-id="element-438">
                <h3 data-id="element-439">
                  #{index + 1} {service.name}
                </h3>
                <p data-id="element-440">
                  {labels.bestFor} {service.bestFor}
                </p>
                <p data-id="element-441">
                  {labels.difficulty}: {service.difficulty} | {labels.speed}:{' '}
                  {service.speed} | {labels.flexibility}: {service.flexibility}{' '}
                  | {labels.integrations}: {service.integrations} | {labels.seo}
                  : {service.seo}
                </p>
                <p data-id="element-442">⭐ {service.rating}/5</p>
                <a href={service.link} data-id="element-443">{t.topServices.visitSite}</a>
              </li>)}
          </ol>
          <p data-id="element-444">{t.topServices.footerText}</p>
        </div>
      </noscript>
    </section>;
}
function ServiceCard({
  service,
  index,
  labels,
  t
}: {
  service: any;
  index: number;
  labels: any;
  t: any;
}) {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const logoChar = SERVICE_LOGOS[service.name] || service.name[0];
  const logoBg = LOGO_COLORS[service.name] || 'bg-gray-600';
  const delayClass = index < 5 ? `delay-${index * 100}` : '';
  return <div ref={ref} className={`group relative bg-[#0A0A0A] rounded-xl border border-white/10 overflow-hidden animate-hidden animate-fade-up ${isVisible ? 'animate-visible' : ''} ${delayClass}`} itemProp="item" itemScope itemType="https://schema.org/SoftwareApplication" data-id="element-445">
      <meta itemProp="name" content={service.name} data-id="element-446" />
      <meta itemProp="applicationCategory" content="WebApplication" data-id="element-447" />
      <meta itemProp="operatingSystem" content="Web" data-id="element-448" />
      <meta itemProp="url" content={service.link} data-id="element-449" />
      <div itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating" data-id="element-450">
        <meta itemProp="ratingValue" content={String(service.rating)} data-id="element-451" />
        <meta itemProp="bestRating" content="5" data-id="element-452" />
        <meta itemProp="worstRating" content="1" data-id="element-453" />
        <meta itemProp="ratingCount" content="150" data-id="element-454" />
      </div>
      <div className="flex flex-col md:flex-row items-center p-5 gap-6" data-id="element-455">
        {/* 1. Rank & Logo */}
        <div className="w-full md:w-[22%] flex-shrink-0" data-id="element-456">
          {/* Desktop */}
          <div className="hidden md:flex items-center gap-4" data-id="element-457">
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-accent-blue/10 text-accent-blue font-bold text-sm" data-id="element-458">
              #{index + 1}
            </div>
            <div className={`w-10 h-10 rounded-lg ${logoBg} flex items-center justify-center overflow-hidden flex-shrink-0`} data-id="element-459">
              <span className="text-white font-bold text-sm" data-id="element-460">{logoChar}</span>
            </div>
            <div data-id="element-461">
              <h3 className="font-bold text-lg text-white leading-tight" data-id="element-462">
                {service.name}
              </h3>
              <span className="inline-block px-2 py-0.5 mt-1 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-gray-400 rounded" data-id="element-463">
                {service.type}
              </span>
            </div>
          </div>
          {/* Mobile */}
          <div className="md:hidden flex items-center justify-between w-full" data-id="element-464">
            <div className="flex items-center gap-3" data-id="element-465">
              <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-blue/10 text-accent-blue font-bold text-xs" data-id="element-466">
                #{index + 1}
              </div>
              <div className={`w-8 h-8 rounded-lg ${logoBg} flex items-center justify-center overflow-hidden flex-shrink-0`} data-id="element-467">
                <span className="text-white font-bold text-xs" data-id="element-468">{logoChar}</span>
              </div>
              <div data-id="element-469">
                <h3 className="font-bold text-base text-white leading-tight" data-id="element-470">
                  {service.name}
                </h3>
                <span className="inline-block px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider bg-white/10 text-gray-400 rounded" data-id="element-471">
                  {service.type}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1" data-id="element-472">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" data-id="element-473" />
              <span className="font-bold text-white" data-id="element-474">{service.rating}</span>
            </div>
          </div>
        </div>

        {/* 2. Features */}
        <div className="flex-1 w-full md:w-auto md:pl-4" data-id="element-475">
          <div className="text-sm text-gray-300 mb-3 text-center" data-id="element-476">
            <span className="text-gray-500 text-xs uppercase mr-2 font-bold" data-id="element-477">
              {labels.bestFor}
            </span>
            <span className="text-white font-medium" data-id="element-478">{service.bestFor}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:flex md:flex-wrap gap-x-5 gap-y-2 text-sm text-gray-400 justify-items-center md:justify-center" data-id="element-479">
            <div className="flex items-center gap-1.5 md:whitespace-nowrap" data-id="element-480">
              <GraduationCap className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-481" />
              <span data-id="element-482">
                {labels.difficulty}:{' '}
                <span className="text-white font-medium" data-id="element-483">
                  {service.difficulty}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-1.5 md:whitespace-nowrap" data-id="element-484">
              <Clock className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-485" />
              <span data-id="element-486">
                {labels.speed}:{' '}
                <span className="text-white font-medium" data-id="element-487">{service.speed}</span>
              </span>
            </div>
            <div className="flex items-center gap-1.5 md:whitespace-nowrap" data-id="element-488">
              <Sliders className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-489" />
              <span data-id="element-490">
                {labels.flexibility}:{' '}
                <span className="text-white font-medium" data-id="element-491">
                  {service.flexibility}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-1.5 md:whitespace-nowrap" data-id="element-492">
              <Plug className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-493" />
              <span data-id="element-494">
                {labels.integrations}:{' '}
                <span className="text-white font-medium" data-id="element-495">
                  {service.integrations}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-1.5 md:whitespace-nowrap" data-id="element-496">
              <Search className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-497" />
              <span data-id="element-498">
                {labels.seo}:{' '}
                <span className="text-white font-medium" data-id="element-499">{service.seo}</span>
              </span>
            </div>
          </div>
        </div>

        {/* 3. Rating & CTA */}
        <div className="w-full md:w-[20%] flex flex-col items-center justify-between gap-3 md:pl-4" data-id="element-500">
          <div className="hidden md:flex flex-col items-center justify-center" data-id="element-501">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-502">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" data-id="element-503" />
              <span className="text-2xl font-bold text-white" data-id="element-504">
                {service.rating}
              </span>
              <span className="text-sm text-gray-500" data-id="element-505">/ 5</span>
            </div>
          </div>
          <a href={service.link} target="_blank" rel="noopener noreferrer" className="w-full py-3 px-4 bg-accent-blue text-white rounded-lg font-medium text-sm hover:bg-accent-hover transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm shadow-accent-blue/20" data-id="element-506">
            {t.topServices.visitSite}
            <Sparkles className="w-3 h-3" data-id="element-507" />
          </a>
        </div>
      </div>
    </div>;
}